/**
 * Discovery API Service
 * Handles trip discovery, recommendations, and seasonal features
 */
import { apiClient } from './apiClient';
import {
  TripRecommendation,
  SeasonalRecommendation,
  RouteSuggestion,
  MultiDestinationResult
} from '../../types/discovery.types';

export const discoveryService = {
  // ============================================================================
  // COLLABORATIVE FILTERING
  // ============================================================================

  /**
   * Get personalized trip recommendations based on user preferences
   */
  getRecommendations: async (params?: {
    limit?: number;
    minSimilarUsers?: number;
  }) => {
    const response = await apiClient.get<{
      recommendations: TripRecommendation[];
    }>('/discovery/collaborative/recommendations', { params });
    return response.data;
  },

  /**
   * Get explanation for why a trip was recommended
   */
  getRecommendationExplanation: async (tripId: string) => {
    const response = await apiClient.get<{ explanation: any }>(
      `/discovery/collaborative/explain/${tripId}`
    );
    return response.data;
  },

  // ============================================================================
  // MULTI-DESTINATION DISCOVERY
  // ============================================================================

  /**
   * Find trips that cover multiple destinations
   */
  searchMultiDestination: async (
    destinations: string[],
    options?: {
      minCoverage?: number;
      exactOrder?: boolean;
      allowAdditionalStops?: boolean;
      limit?: number;
    }
  ) => {
    const response = await apiClient.post<{
      results: MultiDestinationResult[];
    }>('/discovery/multi-destination/search', { destinations, ...options });
    return response.data;
  },

  /**
   * Get route suggestions between two destinations
   */
  suggestRoutes: async (
    startDestination: string,
    endDestination: string,
    options?: {
      maxStops?: number;
      minTripRating?: number;
      limit?: number;
    }
  ) => {
    const response = await apiClient.post<{ routes: RouteSuggestion[] }>(
      '/discovery/multi-destination/routes',
      { startDestination, endDestination, ...options }
    );
    return response.data;
  },

  /**
   * Find trips that can be combined to cover a route
   */
  findCombinableTrips: async (
    destinations: string[],
    options?: {
      maxTrips?: number;
      minOverlap?: number;
      limit?: number;
    }
  ) => {
    const response = await apiClient.post<{ combinations: any[] }>(
      '/discovery/multi-destination/combinations',
      { destinations, ...options }
    );
    return response.data;
  },

  /**
   * Get popular multi-destination routes
   */
  getPopularRoutes: async (params?: {
    minDestinations?: number;
    minTrips?: number;
    limit?: number;
  }) => {
    const response = await apiClient.get<{ routes: any[] }>(
      '/discovery/multi-destination/popular-routes',
      {
        params
      }
    );
    return response.data;
  },

  // ============================================================================
  // SEASONAL DISCOVERY
  // ============================================================================

  /**
   * Get seasonal recommendations for current month
   */
  getCurrentSeason: async (params?: {
    includeWeatherInfo?: boolean;
    limit?: number;
  }) => {
    const response = await apiClient.get<{
      recommendations: SeasonalRecommendation[];
      month: number;
      season: string;
    }>('/discovery/seasonal/current', { params });
    return response.data;
  },

  /**
   * Get recommendations for a specific month
   */
  getSeasonalRecommendations: async (
    month: number,
    params?: {
      hemisphere?: 'northern' | 'southern';
      includeWeatherInfo?: boolean;
      limit?: number;
    }
  ) => {
    const response = await apiClient.get<{
      recommendations: SeasonalRecommendation[];
    }>(`/discovery/seasonal/month/${month}`, { params });
    return response.data;
  },

  /**
   * Get recommendations for a specific season
   */
  getSeasonRecommendations: async (
    season: 'spring' | 'summer' | 'autumn' | 'winter',
    params?: {
      hemisphere?: 'northern' | 'southern';
      limit?: number;
    }
  ) => {
    const response = await apiClient.get<{
      recommendations: SeasonalRecommendation[];
    }>(`/discovery/seasonal/season/${season}`, { params });
    return response.data;
  },

  /**
   * Get hidden gem recommendations for a season
   */
  getHiddenGems: async (
    season: 'spring' | 'summer' | 'autumn' | 'winter',
    params?: {
      maxLikes?: number;
      minRating?: number;
      limit?: number;
    }
  ) => {
    const response = await apiClient.get<{
      recommendations: SeasonalRecommendation[];
    }>(`/discovery/seasonal/hidden-gems/${season}`, { params });
    return response.data;
  },

  /**
   * Get weather-appropriate recommendations
   */
  getWeatherRecommendations: async (
    month: number,
    weatherPreference: 'warm' | 'cool' | 'cold' | 'sunny' | 'mild',
    params?: {
      hemisphere?: 'northern' | 'southern';
      limit?: number;
    }
  ) => {
    const response = await apiClient.get<{
      recommendations: SeasonalRecommendation[];
    }>(`/discovery/seasonal/weather/${month}/${weatherPreference}`, { params });
    return response.data;
  },

  /**
   * Search for trips with specific event/festival keywords
   */
  searchByEvent: async (
    eventKeywords: string[],
    params?: {
      monthRange?: { start: number; end: number };
      limit?: number;
    }
  ) => {
    const response = await apiClient.post<{ recommendations: any[] }>(
      '/discovery/seasonal/events',
      {
        eventKeywords,
        ...params
      }
    );
    return response.data;
  }
};
